import React, { useCallback, useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { helpWhy } from '../../help/why';
import { salesFlow, invoicesOfChallans } from '../../help/flows';
import { SALES_ORDER_STAGES } from '../../help/statusStages';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { DeliveryChallanService, SalesOrderService, fetchAllPages } from '../../services/salesOrder.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { Store } from '../../interfaces/store.interface';
import {
  CHALLAN_STATUS_COLORS, SALES_ORDER_STATUS_COLORS, SalesOrder,
} from '../../interfaces/sales/salesOrder.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ReassignDialog from './scope/ReassignDialog';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { hasDiscount } from '../../utils/salesLineDiscount';   // FW-2A
import { formatFc, formatFcAtRate, isForeignCurrency } from '../../utils/fcDocuments';   // G5b

const SalesOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_ORDER_MANAGE);
  const canClose = usePermission(PERMISSIONS.SALES_ORDER_CLOSE);   // S1
  const canReassign = usePermission(PERMISSIONS.SALES_REASSIGN);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [order, setOrder] = useState<SalesOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [challanOpen, setChallanOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try { setOrder(await SalesOrderService.get(id!)); } catch (e) { toast.showApiError(e, 'Failed to load the order'); }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    try { setBusy(true); await fn(); toast.showSuccess(ok); await load(); } catch (e) { toast.showApiError(e, fail); } finally { setBusy(false); }
  };

  if (!order) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  const s = order.status;
  const accepts = s === 'CONFIRMED' || s === 'PARTIALLY_DELIVERED';
  const tracking = accepts || s === 'DELIVERED';
  const hasBackOrder = order.lines.some(l => l.backOrderQty > 0);
  // The server refuses to cancel an order with deliveries or a draft/posted challan; say so instead of failing on click.
  const hasLiveChallan = (order.challans ?? []).some(c => c.status !== 'CANCELLED') || order.lines.some(l => l.deliveredQty > 0);
  const anyDisc = hasDiscount(order.lines);   // FW-2A
  const fc = isForeignCurrency(order.currencyCode) ? order.currencyCode! : null;   // G5b

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.order.page">
      <FlowStrip data-help="sales.order.flow" stages={salesFlow({ current: 'order', quotation: { id: order.quotationId, no: order.quotationId ? 'Quotation' : null }, order: { no: order.orderNo ?? 'Draft' }, challans: (order.challans ?? []).map(c => ({ id: c.id, no: c.challanNo })), invoices: invoicesOfChallans(order.challans) })} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">
          Sales Order{order.orderNo ? ` ${order.orderNo}` : ''}{' '}
          <StatusStagesChip data-help="sales.order.statusStages" label={humanizeEnum(s)} status={s} stages={SALES_ORDER_STAGES} color={SALES_ORDER_STATUS_COLORS[s]} />
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>Salesperson: {order.salespersonName ?? 'Unassigned'}</Typography>
          {canReassign && <Button data-help="sales.order.reassign" onClick={() => setReassignOpen(true)}>Reassign</Button>}
          {reassignOpen && (
            <ReassignDialog open type="orders" id={order.id} title={`Order ${order.orderNo ?? 'draft'}`} currentName={order.salespersonName}
              onClose={() => setReassignOpen(false)} onDone={load} />
          )}
          {canManage && (s === 'DRAFT' || tracking) && <Button data-help="sales.order.edit" onClick={() => navigate(`/sales/orders/${order.id}/edit`)}>Edit</Button>}
          {canManage && s === 'DRAFT' && <Button data-help="sales.order.confirm" variant="contained" disabled={busy} onClick={() => run(() => SalesOrderService.confirm(order.id), 'Order confirmed', 'Failed to confirm')}>Confirm</Button>}
          {canManage && s === 'DRAFT' && <Button data-help="sales.order.delete" color="error" disabled={busy} onClick={() => run(async () => { await SalesOrderService.remove(order.id); navigate('/sales/orders'); }, 'Draft deleted', 'Failed to delete')}>Delete</Button>}
          {canManage && accepts && hasBackOrder && <Button data-help="sales.order.challan" variant="contained" onClick={() => setChallanOpen(true)}>Create delivery challan</Button>}
          {canClose && tracking && <Button data-help="sales.order.close" disabled={busy} onClick={() => run(() => SalesOrderService.close(order.id), 'Order closed', 'Failed to close')}>Close</Button>}
          {canClose && (s === 'DRAFT' || s === 'CONFIRMED') && (
            <Button data-help="sales.order.cancel" color="error" disabled={hasLiveChallan} onClick={() => setCancelOpen(true)}
              {...helpWhy(hasLiveChallan ? { kind: 'status', message: 'Cancel the delivery challans of this order first.' } : null)}>Cancel order</Button>
          )}
        </Box>
      </Box>
      {order.warnings?.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
      {s === 'CANCELLED' && order.cancelReason && <Alert severity="info" sx={{ mb: 1 }}>Cancelled: {order.cancelReason}</Alert>}
      <Paper data-help-section="sales.order.header" sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <Typography>Customer: <b>{order.customerName}</b></Typography>
        <Typography>Order date: <b>{formatDate(order.orderDate)}</b></Typography>
        <Typography>Requested delivery: <b>{order.requestedDeliveryDate ? formatDate(order.requestedDeliveryDate) : '-'}</b></Typography>
        <Typography>Total: <b>{formatMoney(order.totalAmount)}</b></Typography>
        {fc && (
          <Typography data-testid="so-foreign-total">
            Currency: <b>{formatFcAtRate(fc, Number(order.foreignTotal ?? 0), Number(order.exchangeRate ?? 0), Number(order.totalAmount))}</b>
            {' '}(indicative: the invoice uses the rate of its own date)
          </Typography>
        )}
      </Paper>
      <Paper data-help-section="sales.order.lines" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Lines</Typography>
        <ScrollableTable>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.order.col.lineNo">#</TableCell><TableCell data-help="sales.order.col.item">Item</TableCell><TableCell data-help="sales.order.col.description">Description</TableCell>
                <TableCell align="right" data-help="sales.order.col.ordered">Ordered</TableCell><TableCell align="right" data-help="sales.order.col.delivered">Delivered</TableCell>
                <TableCell align="right" data-help="sales.order.col.invoiced">Invoiced</TableCell><TableCell align="right" data-help="sales.order.col.backOrder">Back-order</TableCell>
                <TableCell align="right" data-help="sales.order.col.unitPrice">Unit price</TableCell>{fc && <TableCell align="right" data-help="sales.order.col.fcUnitPrice">{`Unit price (${fc})`}</TableCell>}{fc && <TableCell align="right" data-help="sales.order.col.fcNet">{`Net (${fc})`}</TableCell>}{anyDisc && <TableCell align="right" data-help="sales.order.col.discount">Discount</TableCell>}<TableCell align="right" data-help="sales.order.col.total">{anyDisc ? 'Net total' : 'Total'}</TableCell>   {/* FW-2A */}
              </TableRow>
            </TableHead>
            <TableBody>
              {order.lines.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{l.lineNo}</TableCell>
                  <TableCell>{l.itemCode ? `${l.itemCode} - ${l.itemName}` : 'Service'}</TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell align="right">{l.quantity}</TableCell>
                  <TableCell align="right">{l.deliveredQty}</TableCell>
                  <TableCell align="right">{l.invoicedQty}</TableCell>
                  <TableCell align="right">{l.backOrderQty}</TableCell>
                  <TableCell align="right">{formatMoney(l.unitPrice)}</TableCell>
                  {fc && <TableCell align="right">{formatFc(fc, l.foreignUnitPrice)}</TableCell>}
                  {fc && <TableCell align="right">{formatFc(fc, l.foreignAmount)}</TableCell>}
                  {anyDisc && <TableCell align="right">{Number(l.discountAmount) > 0 ? `${formatMoney(l.discountAmount)}${l.discountPct != null ? ` (${l.discountPct}%)` : ''}` : '-'}</TableCell>}   {/* FW-2A */}
                  <TableCell align="right">{formatMoney(l.lineTotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      </Paper>
      <Paper data-help-section="sales.order.challans" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Delivery challans</Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="sales.order.challans.col.number">Challan #</TableCell><TableCell data-help="sales.order.challans.col.date">Date</TableCell><TableCell data-help="sales.order.challans.col.store">Store</TableCell><TableCell data-help="sales.order.challans.col.status">Status</TableCell><TableCell data-help="sales.order.challans.col.invoice">Invoice</TableCell></TableRow></TableHead>
          <TableBody>
            {order.challans.map(c => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/challans/${c.id}`)}>
                <TableCell>{c.challanNo ?? 'Draft'}</TableCell><TableCell>{formatDate(c.challanDate)}</TableCell>
                <TableCell>{c.storeName}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(c.status)} color={CHALLAN_STATUS_COLORS[c.status]} /></TableCell>
                <TableCell>{c.invoiceNumber ?? (c.salesInvoiceId ? 'Draft invoice' : '-')}</TableCell>
              </TableRow>
            ))}
            {order.challans.length === 0 && <TableRow><TableCell colSpan={5}>No challans yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      {challanOpen && <NewChallanDialog order={order} onClose={() => setChallanOpen(false)} onCreated={cid => navigate(`/sales/challans/${cid}`)} />}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel order</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField data-help="sales.order.cancel.reason" label="Reason" fullWidth value={reason} onChange={e => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.order.cancel.back" onClick={() => setCancelOpen(false)}>Back</Button>
          <Button color="error" data-help="sales.order.cancel.confirm" disabled={!reason.trim() || busy} onClick={() => { setCancelOpen(false); run(() => SalesOrderService.cancel(order.id, reason), 'Order cancelled', 'Failed to cancel'); }}>Cancel order</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const NewChallanDialog: React.FC<{ order: SalesOrder; onClose: () => void; onCreated: (id: string) => void }> = ({ order, onClose, onCreated }) => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [vehicle, setVehicle] = useState('');
  const [driver, setDriver] = useState('');
  const [qty, setQty] = useState<Record<string, number>>(
    Object.fromEntries(order.lines.filter(l => l.backOrderQty > 0).map(l => [l.id, l.backOrderQty])));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAllPages<Store>((p, n) => getStoresByInstitutionId(p, n, 'ALL')).then(setStores).catch(() => setStores([]));
    SalesOrderService.getSettings(institutionId).then(s => { if (s.defaultStoreId) setStoreId(p => p || s.defaultStoreId!); }).catch(() => undefined);
  }, [institutionId]);

  const lines = order.lines.filter(l => l.backOrderQty > 0);
  const picked = lines.filter(l => qty[l.id] > 0);
  const over = lines.some(l => qty[l.id] > l.backOrderQty);

  const create = async () => {
    try {
      setSaving(true);
      const c = await DeliveryChallanService.create(institutionId, {
        salesOrderId: order.id, storeId, challanDate: date, vehicleNo: vehicle || undefined, driverName: driver || undefined,
        lines: picked.map(l => ({ salesOrderLineId: l.id, quantity: qty[l.id] })),
      });
      toast.showSuccess('Draft challan created');
      onCreated(c.id);
    } catch (e) {
      toast.showApiError(e, 'Failed to create the challan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New delivery challan</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <SearchableSelect data-help="sales.order.challanDlg.store" label="Deliver from store" required fullWidth options={stores} value={storeId || null} onChange={id => setStoreId(id)} getOptionLabel={s => s.storeName} />
        <TextField data-help="sales.order.challanDlg.date" label="Challan date" type="date" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField data-help="sales.order.challanDlg.vehicle" label="Vehicle no." value={vehicle} onChange={e => setVehicle(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 240 }} />
          <TextField data-help="sales.order.challanDlg.driver" label="Driver" value={driver} onChange={e => setDriver(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 240 }} />
        </Box>
        {lines.map(l => (
          <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ flex: 1 }}>{l.description} (left {l.backOrderQty})</Typography>
            <TextField size="small" data-help="sales.order.challanDlg.qty" type="number" sx={{ width: 100 }} label="Qty" inputProps={{ 'aria-label': `Quantity to deliver: ${l.description}`, min: 0, step: 'any' }}
              value={qty[l.id] ?? ''} error={qty[l.id] > l.backOrderQty}
              onChange={e => setQty({ ...qty, [l.id]: e.target.value === '' ? 0 : Number(e.target.value) })} />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.order.challanDlg.cancel" onClick={onClose}>Cancel</Button>
        <Button variant="contained" data-help="sales.order.challanDlg.create" disabled={!storeId || picked.length === 0 || over || saving} onClick={create}>Create draft</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesOrderDetail;
